import React, { memo, useCallback, useMemo } from "react";
import { FaPlus, FaMinus, FaClock } from "react-icons/fa";
import { MenuItem } from "../redux/slices/menuItemsSlice";
import { formatPrice, parsePrice } from "../utils/priceUtils";
import { getCartQuantity, getCartItemId } from "../utils/cartUtils";

interface BasicMenuItemCardProps {
  item: MenuItem;
  onItemClick?: (item: MenuItem) => void;
  onAddToCart?: (item: MenuItem) => void;
  showAddToCart?: boolean;
  cartItems?: any[];
  onUpdateQuantity?: (cartItemId: string, quantity: number) => void;
  onIncrementQuantity?: (item: MenuItem) => void;
  onDecrementQuantity?: (itemId: string) => void;
}

// Subcomponent: Enhanced Menu Item Image with Contextual Badge Experience
const MenuItemImage: React.FC<{
  item: MenuItem;
  onImageError: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}> = ({ item, onImageError }) => {
  // Split parts using "||" and convert any date strings into friendly format (e.g., "Oct 9, 2026")
  const cleanedParts = useMemo(() => {
    if (!item.mealTimeRange) return [];
    return item.mealTimeRange
      .split("||")
      .map((rawPart: string): string => rawPart.trim())
      .filter(Boolean)
      .map((partStr: string): string => {
        const ddMmYyyyMatch = partStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        const yyyyMmDdMatch = partStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);

        let year = "";
        let month = "";
        let day = "";

        if (ddMmYyyyMatch) {
          day = ddMmYyyyMatch[1];
          month = ddMmYyyyMatch[2];
          year = ddMmYyyyMatch[3];
        } else if (yyyyMmDdMatch) {
          year = yyyyMmDdMatch[1];
          month = yyyyMmDdMatch[2];
          day = yyyyMmDdMatch[3];
        }

        if (year && month && day) {
          const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
          }
        }

        return partStr;
      });
  }, [item.mealTimeRange]);

  return (
    <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
      {item.image ? (
        <img
          src={item.image}
          alt={`${item.name} delicious menu item available for order`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={onImageError}
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
          <span
            className="text-4xl font-bold uppercase"
            style={{ color: '#FA2C37' }}
          >
            {item.name.charAt(0)}
          </span>
        </div>
      )}

      {/* Top Right / Left Badge Container */}
      {item.MealName && item.MealName !== "All Day" && (
        <div 
          className="absolute top-2.5 left-2.5 max-w-[calc(100%-1.25rem)] backdrop-blur-md text-xs px-3 py-2 rounded-xl flex items-start gap-2.5 shadow-lg z-10 transition-all duration-300 border bg-white/95 text-gray-800 border-white/40 shadow-black/5"
          role="status"
          aria-label={`Available during ${item.MealName}`}
        >
          <div className="relative flex items-center justify-center mt-0.5 flex-shrink-0">
            <FaClock className="w-3.5 h-3.5 text-amber-500" />
            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-emerald-500 rounded-full ring-2 ring-white" />
          </div>

          <div className="flex flex-col gap-0.5 overflow-hidden">
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-xs truncate text-gray-900">
                {item.MealName}
              </span>
            </div>
            
            {cleanedParts.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap text-[10px] font-medium text-gray-500">
                {cleanedParts.map((partItem: string, idx: number) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span className="text-gray-300">•</span>}
                    <span className={idx === 0 ? "text-red-600 font-semibold" : "text-current opacity-90"}>
                      {partItem}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Subcomponent: Menu Item Info
const MenuItemInfo: React.FC<{ item: MenuItem }> = ({ item }) => {
  const displayPrice = typeof item.price === 'object' ? item.price.online : item.price;
  
  return (
    <div className="p-4 pb-3 flex-grow flex flex-col">
      <h3 className="text-lg font-semibold line-clamp-1 leading-tight mb-2 transition-colors text-gray-900">
        {item.name}
      </h3>

      {item.description && (
        <p className="text-sm leading-relaxed mb-3 line-clamp-2 text-gray-600">
          {item.description}
        </p>
      )}

      <div className="mt-auto">
        <span className="text-xl font-bold text-gray-900">
          {formatPrice(displayPrice)}
        </span>
      </div>
    </div>
  );
};

// Subcomponent: Add to Cart Button
const AddToCartButton: React.FC<{
  item: MenuItem;
  isOutOfStock: boolean;
  isExpired: boolean;
  showAddToCart: boolean;
  cartQuantity: number;
  cartItemId: string;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onUpdateQuantity?: (cartItemId: string, quantity: number) => void;
  onIncrementQuantity?: (item: MenuItem) => void;
  onDecrementQuantity?: (itemId: string) => void;
}> = ({ item, isOutOfStock, isExpired, showAddToCart, cartQuantity, cartItemId, onAddToCart, onUpdateQuantity, onIncrementQuantity, onDecrementQuantity }) => {
  if (!showAddToCart) return null;

  // Hide add to cart if meal time is expired
  if (isExpired) return null;

  const handleMinus = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDecrementQuantity) {
      onDecrementQuantity(item.pk_id.toString());
    } else if (onUpdateQuantity) {
      onUpdateQuantity(cartItemId, cartQuantity - 1);
    }
  }, [onDecrementQuantity, onUpdateQuantity, item.pk_id, cartItemId, cartQuantity]);

  const handlePlus = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (cartQuantity > 0) {
      if (onIncrementQuantity) {
        onIncrementQuantity(item);
      } else if (onUpdateQuantity) {
        onUpdateQuantity(cartItemId, cartQuantity + 1);
      }
    } else {
      onAddToCart(e as any);
    }
  }, [cartQuantity, onIncrementQuantity, onUpdateQuantity, item, cartItemId, onAddToCart]);

  if (cartQuantity > 0) {
    return (
      <div className="w-full py-3 px-4 flex items-center justify-center gap-3 bg-red-500 rounded-b-xl min-h-[48px]">
        <button
          onClick={handleMinus}
          className="p-1 hover:scale-110 focus:outline-none transition-transform"
          aria-label="Decrease quantity"
        >
          <FaMinus className="w-4 h-4 text-white" />
        </button>
        <span className="font-bold text-xl text-white min-w-[2rem] text-center">{cartQuantity}</span>
        <button
          onClick={handlePlus}
          className="p-1 hover:scale-110 focus:outline-none transition-transform"
          aria-label="Increase quantity"
        >
          <FaPlus className="w-4 h-4 text-white" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={isOutOfStock}
      className={`w-full py-3 px-4 text-base font-bold transition-all duration-200 rounded-b-xl min-h-[48px] focus:outline-none ${
        isOutOfStock
          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
          : 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700 hover:shadow-md'
      }`}
      onClick={onAddToCart}
      aria-label={`Add ${item.name} to cart${isOutOfStock ? ' - Out of Stock' : ''}`}
      aria-disabled={isOutOfStock}
      data-testid={`add-to-cart-${item.id || item.pk_id}`}
    >
      {isOutOfStock ? 'Out of Stock' : 'Add'}
    </button>
  );
};

const BasicMenuItemCard: React.FC<BasicMenuItemCardProps> = ({
  item,
  onItemClick,
  onAddToCart,
  showAddToCart = true,
  cartItems = [],
  onUpdateQuantity,
  onIncrementQuantity,
  onDecrementQuantity,
}) => {
  if (!item.is_enabled || !item.is_item_verified) {
    return null;
  }

  const isOutOfStock = !item.inventoryStatus;
  const isExpired = Boolean(
    item.isMealTimeExpired || 
    (item.isMealTimeActive === false) || 
    (item.MealName !== "All Day" && !item.isMealTimeActive)
  );

  const cartQuantity = useMemo(() => 
    getCartQuantity(item.pk_id.toString(), cartItems),
    [item.pk_id, cartItems]
  );

  const cartItemId = useMemo(() => 
    getCartItemId(item.pk_id.toString()),
    [item.pk_id]
  );

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      (e.target as HTMLImageElement).src = `https://via.placeholder.com/300x200/f8f9fa/6c757d?text=${encodeURIComponent(
        item.name
      )}`;
    },
    [item.name]
  );

  const handleCardClick = useCallback(() => {
    onItemClick?.(item);
  }, [onItemClick, item]);

  const handleAddToCart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!isOutOfStock && !isExpired) {
        onAddToCart?.(item);
      }
    },
    [onAddToCart, item, isOutOfStock, isExpired]
  );

  const displayPrice = typeof item.price === 'object' ? item.price.online : item.price;

  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 flex flex-col hover:shadow-lg hover:-translate-y-1 cursor-pointer group"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${item.name}, priced at ${parsePrice(displayPrice)}${isOutOfStock ? ' - Out of Stock' : ''}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleCardClick();
      }}
      data-testid={`menu-item-card-${item.id || item.pk_id}`}
    >
      <MenuItemImage 
        item={item} 
        onImageError={handleImageError} 
      />
      <MenuItemInfo item={item} />
      <AddToCartButton 
        item={item}
        isOutOfStock={isOutOfStock}
        isExpired={isExpired}
        showAddToCart={showAddToCart}
        cartQuantity={cartQuantity}
        cartItemId={cartItemId}
        onAddToCart={handleAddToCart}
        onUpdateQuantity={onUpdateQuantity}
        onIncrementQuantity={onIncrementQuantity}
        onDecrementQuantity={onDecrementQuantity}
      />
    </div>
  );
};

const areEqual = (prev: BasicMenuItemCardProps, next: BasicMenuItemCardProps) => {
  return (
    (prev.item.id || prev.item.pk_id) === (next.item.id || next.item.pk_id) &&
    prev.item.price === next.item.price &&
    prev.item.is_enabled === next.item.is_enabled &&
    prev.item.inventoryStatus === next.item.inventoryStatus &&
    prev.item.is_item_verified === next.item.is_item_verified &&
    prev.item.MealName === next.item.MealName &&
    prev.item.mealTimeRange === next.item.mealTimeRange &&
    prev.item.isMealTimeActive === next.item.isMealTimeActive &&
    prev.item.isMealTimeExpired === next.item.isMealTimeExpired &&
    prev.showAddToCart === next.showAddToCart &&
    prev.cartItems === next.cartItems
  );
};

export default memo(BasicMenuItemCard, areEqual);